// src/components/compliance/modals/ConvertedRecommendationHandoffsModal.jsx
// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.

import React, { useCallback, useEffect, useState } from "react";
import ModalShell from "./shared/ModalShell";
import ModalSection from "./shared/ModalSection";
import ModalActionRow from "./shared/ModalActionRow";

const CONNECTOR_PROVIDER_DISPLAY_NAMES = {
  aws: "AWS",
  cloudflare: "Cloudflare",
  github: "GitHub",
  google_workspace: "Google Workspace",
  okta: "Okta",
};

function cleanConnectorProvider(value) {
  return String(value || "").trim().toLowerCase();
}

function formatConnectorProvider(value) {
  const normalizedProvider = cleanConnectorProvider(value);

  if (CONNECTOR_PROVIDER_DISPLAY_NAMES[normalizedProvider]) {
    return CONNECTOR_PROVIDER_DISPLAY_NAMES[normalizedProvider];
  }

  return String(value || "Unknown")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function titleCaseLabel(value, fallback = "—") {
  const text = String(value || "").trim();
  if (!text) return fallback;
  return text
    .replace(/_/g, " ")
    .replace(/-/g, " ")
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function normalizeList(value) {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.filter((item) => item !== undefined && item !== null && item !== "");
  }
  return [value].filter((item) => item !== undefined && item !== null && item !== "");
}

function formatList(value, fallback = "—") {
  const items = normalizeList(value)
    .map((item) => String(item).trim())
    .filter(Boolean);

  return items.length ? items.join(", ") : fallback;
}

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString();
}

function formatPriority(value) {
  const normalized = String(value || "medium").trim().toLowerCase();
  if (normalized === "critical") return "Critical";
  if (normalized === "high") return "High";
  if (normalized === "low") return "Low";
  return "Medium";
}

function priorityColor(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "critical" || normalized === "high") return "#fb7185";
  if (normalized === "medium") return "#fbbf24";
  return "#34d399";
}

function shortId(value, fallback = "—") {
  const text = String(value || "").trim();
  if (!text) return fallback;
  if (text.length <= 14) return text;
  return `${text.slice(0, 8)}…${text.slice(-4)}`;
}

function formatRefreshError(error) {
  const text = String(error || "").trim();
  if (!text) return "";

  if (text.toLowerCase() === "failed to fetch") {
    return "Converted handoffs could not be refreshed. Check the API connection and try again.";
  }

  return text;
}

function conversionPayload(item = {}) {
  const sourcePreview = item.source_preview || {};
  return sourcePreview.conversion || {};
}

const summaryBarStyle = {
  border: "1px solid rgba(148, 163, 184, 0.18)",
  borderRadius: 16,
  padding: "12px 14px",
  background: "rgba(15, 23, 42, 0.44)",
  color: "#cbd5e1",
  display: "flex",
  justifyContent: "space-between",
  gap: 12,
  alignItems: "center",
  flexWrap: "wrap",
};

const summaryCountStyle = {
  color: "#f8fafc",
  fontSize: 14,
  fontWeight: 900,
};

const summaryHintStyle = {
  color: "#cbd5e1",
  fontSize: 13,
  lineHeight: 1.45,
};

const listStyle = {
  display: "grid",
  gap: 12,
};

const itemCardStyle = {
  border: "1px solid rgba(148, 163, 184, 0.18)",
  borderRadius: 16,
  padding: 16,
  background: "rgba(15, 23, 42, 0.46)",
  display: "grid",
  gap: 12,
};

const chipRowStyle = {
  display: "flex",
  gap: 8,
  flexWrap: "wrap",
  alignItems: "center",
  justifyContent: "space-between",
};

const chipStyle = {
  borderRadius: 999,
  padding: "4px 9px",
  fontSize: 11,
  fontWeight: 900,
  background: "rgba(30, 41, 59, 0.64)",
  border: "1px solid rgba(148, 163, 184, 0.20)",
  color: "#cbd5e1",
};

const detailGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: 10,
  color: "#cbd5e1",
  fontSize: 13,
};

const bodyTextStyle = {
  color: "#cbd5e1",
  fontSize: 13,
  lineHeight: 1.55,
  margin: 0,
};

const noticeStyle = {
  border: "1px solid rgba(148, 163, 184, 0.18)",
  borderRadius: 14,
  padding: "10px 12px",
  background: "rgba(15, 23, 42, 0.48)",
  color: "#cbd5e1",
  fontSize: 13,
  lineHeight: 1.45,
};

const successNoticeStyle = {
  ...noticeStyle,
  border: "1px solid rgba(52, 211, 153, 0.32)",
  background: "rgba(6, 78, 59, 0.16)",
  color: "#bbf7d0",
};

const auditNoticeStyle = {
  ...noticeStyle,
  border: "1px solid rgba(251, 191, 36, 0.28)",
  background: "rgba(120, 53, 15, 0.12)",
  color: "#fde68a",
};

const errorStyle = {
  ...noticeStyle,
  border: "1px solid rgba(248, 113, 113, 0.38)",
  background: "rgba(127, 29, 29, 0.20)",
  color: "#fecaca",
};

export default function ConvertedRecommendationHandoffsModal({
  open,
  items = [],
  count = 0,
  loading = false,
  error = "",
  message = "",
  onClose,
  onRefresh,
}) {
  const convertedItems = Array.isArray(items) ? items : [];
  const convertedCount = Number.isFinite(Number(count)) ? Number(count) : convertedItems.length;
  const formattedError = formatRefreshError(error);
  const [refreshPulse, setRefreshPulse] = useState(false);
  const isRefreshing = Boolean(loading || refreshPulse);

  useEffect(() => {
    if (!open) {
      setRefreshPulse(false);
    }
  }, [open]);

  const handleRefresh = useCallback(() => {
    if (isRefreshing) return;

    setRefreshPulse(true);
    onRefresh?.();

    window.setTimeout(() => {
      setRefreshPulse(false);
    }, 1200);
  }, [isRefreshing, onRefresh]);

  return (
    <ModalShell
      open={open}
      ariaLabel="Converted recommendation handoffs"
      eyebrow="OASYS · Converted Handoffs"
      title="Converted Recommendation Handoffs"
      subtitle="Review handoffs that have been linked to remediation task tracking. These records are audit/recall only."
      onClose={onClose}
      maxWidth="960px"
      accentBorder="rgba(96, 165, 250, 0.42)"
      zIndex={170}
    >
      <div style={summaryBarStyle}>
        <div>
          <div style={summaryCountStyle}>
            {convertedCount} converted handoff{convertedCount === 1 ? "" : "s"} available for recall
          </div>
          <div style={summaryHintStyle}>
            {isRefreshing
              ? "Refreshing converted handoffs…"
              : "Audit history for handoffs linked to remediation tracking. No operational execution occurs from this view."}
          </div>
        </div>

        <button
          type="button"
          className="btn btn-ghost btn-xs"
          onClick={handleRefresh}
          disabled={isRefreshing}
          style={{ whiteSpace: "nowrap" }}
        >
          {isRefreshing ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {message ? <div style={noticeStyle}>{message}</div> : null}
      {formattedError && !isRefreshing ? <div style={errorStyle}>{formattedError}</div> : null}

      <ModalSection
        title="Converted handoff recall"
        description="These handoffs are historical conversion records. They show which recommendation handoffs were linked to remediation task tracking."
        badge={isRefreshing ? "Refreshing" : `${convertedItems.length} converted`}
        badgeTone="medium"
      >
        {isRefreshing ? (
          <p style={bodyTextStyle}>Refreshing converted handoffs…</p>
        ) : convertedItems.length === 0 ? (
          <p style={bodyTextStyle}>No converted handoffs are available yet.</p>
        ) : (
          <div style={listStyle}>
            {convertedItems.map((item) => {
              const conversion = conversionPayload(item);
              const priority = formatPriority(item.priority);
              const connector = item.connector ? formatConnectorProvider(item.connector) : "No connector";
              const taskIds = normalizeList(item.related_task_ids);
              const primaryTaskId = conversion.remediation_task_id || taskIds[0] || "";

              return (
                <article key={item.id || item.recommendation_key || item.title} style={itemCardStyle}>
                  <div style={chipRowStyle}>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                      <span
                        style={{
                          ...chipStyle,
                          color: "#bbf7d0",
                          borderColor: "rgba(52, 211, 153, 0.30)",
                          background: "rgba(6, 78, 59, 0.16)",
                        }}
                      >
                        Converted
                      </span>
                      <span
                        style={{
                          ...chipStyle,
                          color: priorityColor(item.priority),
                          borderColor: priorityColor(item.priority),
                          background: "rgba(15, 23, 42, 0.36)",
                        }}
                      >
                        {priority}
                      </span>
                      <span style={chipStyle}>{titleCaseLabel(item.category || item.handoff_type)}</span>
                      <span style={chipStyle}>{connector}</span>
                      <span style={chipStyle}>
                        {conversion.existing_task ? "Existing task linked" : "Task created"}
                      </span>
                    </div>
                    <strong style={{ color: "#cbd5e1", fontSize: 12 }}>
                      Converted {formatDateTime(item.converted_at)}
                    </strong>
                  </div>

                  <div style={{ display: "grid", gap: 6 }}>
                    <strong style={{ color: "#f8fafc", fontSize: 15, lineHeight: 1.35 }}>
                      {item.title || item.suggested_workflow_title || "Converted recommendation handoff"}
                    </strong>
                    <p style={bodyTextStyle}>
                      {item.summary || "Converted handoff linked to remediation task tracking."}
                    </p>
                  </div>

                  <div style={detailGridStyle}>
                    <div>
                      <strong>Framework:</strong> {item.framework || "—"}
                    </div>
                    <div>
                      <strong>Controls:</strong> {formatList(item.controls)}
                    </div>
                    <div>
                      <strong>Findings:</strong> {formatList(item.findings)}
                    </div>
                    <div>
                      <strong>Resources:</strong> {formatList(item.resources)}
                    </div>
                    <div>
                      <strong>Linked task:</strong> {primaryTaskId ? shortId(primaryTaskId) : "—"}
                    </div>
                    <div>
                      <strong>Converted by:</strong> {conversion.converted_by_name || item.created_by_name || "—"}
                    </div>
                  </div>

                  {primaryTaskId ? (
                    <div style={successNoticeStyle}>
                      Linked remediation task ID: {primaryTaskId}
                    </div>
                  ) : null}

                  <div style={auditNoticeStyle}>
                    Audit only. OASYS did not execute remediation, close a task, approve evidence, advance workflow, or run validation from this recall record.
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </ModalSection>

      <ModalActionRow
        actions={[
          {
            key: "close",
            label: "Close",
            className: "btn btn-primary btn-sm",
            onClick: onClose,
          },
        ]}
      />
    </ModalShell>
  );
}
